import { useState, useCallback, createContext, useContext, type ReactNode } from 'react';
import './UpsellModal.css';

/**
 * Feature descriptions for upsell messaging.
 */
const FEATURE_INFO: Record<string, { title: string; description: string; icon: string }> = {
    EMPLOYEE_LIMIT: {
        title: 'Límite de Empleados Alcanzado',
        description: 'Tu plan actual no permite agregar más empleados. Actualiza para hacer crecer tu equipo.',
        icon: '👥',
    },
    CLIENT_LIMIT: {
        title: 'Límite de Clientes Alcanzado',
        description: 'Has alcanzado el máximo de clientes. Actualiza para no perder ninguna oportunidad.',
        icon: '📇',
    },
    PRODUCT_LIMIT: {
        title: 'Límite de Productos Alcanzado',
        description: 'Tu inventario está al máximo. Actualiza para gestionar más productos.',
        icon: '📦',
    },
    STORAGE_LIMIT: {
        title: 'Límite de Ubicaciones de Almacenamiento',
        description: 'Necesitas más ubicaciones de inventario. Actualiza tu plan.',
        icon: '🏪',
    },
    WHATSAPP_LIMIT: {
        title: 'Mensajes de WhatsApp Agotados',
        description: 'Has usado todos tus mensajes de WhatsApp este mes. Actualiza para más.',
        icon: '💬',
    },
    PHOTO_UPLOAD: {
        title: 'Fotos de Empleados',
        description: 'Sube fotos de tu equipo para una experiencia más personal.',
        icon: '📸',
    },
    VISUAL_HISTORY: {
        title: 'Historial Visual (Antes/Después)',
        description: 'Documenta las transformaciones de tus clientes con fotos.',
        icon: '🖼️',
    },
    CUSTOM_BRANDING: {
        title: 'Personalización de Marca',
        description: 'Logo y colores personalizados para tu salón.',
        icon: '🎨',
    },
    ADVANCED_REPORTS: {
        title: 'Reportes Avanzados',
        description: 'Métricas detalladas y exportación a PDF/Excel.',
        icon: '📊',
    },
    PERMISSION_PANEL: {
        title: 'Panel de Permisos',
        description: 'Controla exactamente qué puede hacer cada miembro de tu equipo.',
        icon: '🔐',
    },
    API_ACCESS: {
        title: 'Acceso a API',
        description: 'Integra Cresta con tus otras herramientas.',
        icon: '🔌',
    },
    DEFAULT: {
        title: 'Funcionalidad Premium',
        description: 'Esta característica requiere un plan superior.',
        icon: '⭐',
    },
};

const PLAN_BENEFITS = [
    { icon: '✨', text: 'Más empleados y clientes' },
    { icon: '💬', text: 'Notificaciones por WhatsApp' },
    { icon: '📊', text: 'Reportes avanzados' },
    { icon: '🎨', text: 'Personalización de marca' },
    { icon: '🔐', text: 'Control de permisos' },
];

interface UpsellModalContextType {
    showUpsell: (limitType: string) => void;
    hideUpsell: () => void;
    isVisible: boolean;
}

const UpsellModalContext = createContext<UpsellModalContextType | undefined>(undefined);

export const useUpsellModal = () => {
    const context = useContext(UpsellModalContext);
    if (!context) {
        throw new Error('useUpsellModal must be used within UpsellModalProvider');
    }
    return context;
};

interface UpsellModalProviderProps {
    children: ReactNode;
}

export const UpsellModalProvider = ({ children }: UpsellModalProviderProps) => {
    const [isVisible, setIsVisible] = useState(false);
    const [limitType, setLimitType] = useState<string>('DEFAULT');

    const showUpsell = useCallback((type: string) => {
        setLimitType(type);
        setIsVisible(true);
    }, []);

    const hideUpsell = useCallback(() => {
        setIsVisible(false);
    }, []);

    const isUnlimited = import.meta.env.VITE_BETA_MODE === 'true' || import.meta.env.VITE_FREE_MODE === 'true';
    const isBetaMode = isUnlimited; // Alias for backward compatibility in template below
    const featureInfo = FEATURE_INFO[limitType] || FEATURE_INFO.DEFAULT;

    return (
        <UpsellModalContext.Provider value={{ showUpsell, hideUpsell, isVisible }}>
            {children}

            {/* Modal Overlay */}
            {isVisible && (
                <div className="upsell-overlay" onClick={hideUpsell}>
                    <div className="upsell-modal" onClick={(e) => e.stopPropagation()}>
                        {/* Close Button */}
                        <button className="upsell-close" onClick={hideUpsell} aria-label="Cerrar">
                            ✕
                        </button>

                        {/* Header */}
                        <div className="upsell-header">
                            <span className="upsell-icon">{isBetaMode ? '✨' : featureInfo.icon}</span>
                            <h2 className="upsell-title">{isBetaMode ? 'Función Premium Activa' : featureInfo.title}</h2>
                            <p className="upsell-description">
                                {isBetaMode 
                                    ? 'Estás en modo BETA. Todas las funciones Premium están desbloqueadas para ti sin costo adicional.'
                                    : featureInfo.description
                                }
                            </p>
                        </div>

                        {/* Benefits */}
                        {!isBetaMode && (
                            <div className="upsell-benefits">
                                <h3 className="upsell-benefits-title">Al actualizar obtienes:</h3>
                                <ul className="upsell-benefits-list">
                                    {PLAN_BENEFITS.map((benefit, index) => (
                                        <li key={index} className="upsell-benefit-item">
                                            <span className="benefit-icon">{benefit.icon}</span>
                                            <span className="benefit-text">{benefit.text}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* CTA */}
                        <div className="upsell-cta">
                            {isBetaMode ? (
                                <button className="upsell-button-primary" onClick={hideUpsell}>
                                    Entendido, ¡gracias!
                                </button>
                            ) : (
                                <>
                                    <button className="upsell-button-primary" onClick={() => {
                                        window.location.href = '/precios';
                                    }}>
                                        Ver Planes Disponibles
                                    </button>
                                    <button className="upsell-button-secondary" onClick={hideUpsell}>
                                        Quizás después
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Footer Note */}
                        <p className="upsell-footer-note">
                            {isBetaMode 
                                ? '💡 Aprovecha Cresta PRO mientras dure la Beta'
                                : '💡 Puedes probar 14 días gratis en cualquier plan'
                            }
                        </p>
                    </div>
                </div>
            )}
        </UpsellModalContext.Provider>
    );
};

export default UpsellModalProvider;
